import { createClient, type SupabaseClient } from '@supabase/supabase-js';

function isPlaceholder(value: string | undefined): boolean {
  return !value || value.startsWith('your-') || value.startsWith('placeholder');
}

let _supabase: SupabaseClient | null = null;

/**
 * Override-mekanism för Deno edge fn:s, där `import.meta.env` inte finns.
 * Edge fn skapar sin egen service-role-klient och registrerar den här
 * INNAN något shared-module-anrop sker. Browser-kod använder fortsatt
 * `getSupabase()` utan att veta om setter:n.
 */
export function setSupabaseClient(client: SupabaseClient): void {
  _supabase = client;
}

function readViteEnv(): { url: string | undefined; anonKey: string | undefined } {
  // I Deno-runtime är `import.meta.env` undefined → bracket-access kastar.
  // Try/catch gör så att server-side-import inte sprängs vid module-load.
  try {
    return {
      url: import.meta.env['VITE_SUPABASE_URL'] as string | undefined,
      anonKey: import.meta.env['VITE_SUPABASE_ANON_KEY'] as string | undefined,
    };
  } catch {
    return { url: undefined, anonKey: undefined };
  }
}

export function getSupabase(): SupabaseClient {
  if (_supabase) return _supabase;

  const { url, anonKey } = readViteEnv();
  if (isPlaceholder(url) || isPlaceholder(anonKey)) {
    throw new Error(
      'Supabase is not configured. Update VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in .env',
    );
  }
  // Native-appen (Capacitor) kör PKCE: mejl-länkar/deep links levererar en
  // ?code= som växlas i native-init.ts. Webben behåller implicit-flödet
  // (hash-tokens), beprövad väg, och PKCE:s code_verifier skulle strandas
  // när mejl-länkar öppnas i en annan webbläsare än den som initierade.
  // OBS: ingen @capacitor/core-import här, filen delas med Deno edge-fn:s;
  // Capacitor injicerar en global i native-webviewen som räcker för detektion.
  const isNative =
    typeof window !== 'undefined' &&
    Boolean(
      (
        window as { Capacitor?: { isNativePlatform?: () => boolean } }
      ).Capacitor?.isNativePlatform?.(),
    );
  _supabase = createClient(url!, anonKey!, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: isNative ? 'pkce' : 'implicit',
    },
  });
  return _supabase;
}

/** Browser-tid-check. Lazy så Deno aldrig läser env vid import. */
export function getIsSupabaseConfigured(): boolean {
  const { url, anonKey } = readViteEnv();
  return !isPlaceholder(url) && !isPlaceholder(anonKey);
}

/** Bakåtkompat: behåll konstant-exportet för befintliga browser-call-sites.
 * Beräknas lazy på första access i browser; i Deno är det alltid false (men
 * vi anropar setSupabaseClient innan something använder det). */
export const isSupabaseConfigured: boolean = (() => {
  try {
    return getIsSupabaseConfigured();
  } catch {
    return false;
  }
})();
